import { MouseEvent, useCallback, useMemo } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Divider, ListItemIcon, ListItemText, Menu, Tooltip } from '@mui/material';

import { ReactComponent as GroupIcon } from '@/assets/icons/GroupWorkRounded.svg';
import { MAX_SORT_COLUMNS } from '@/shared/lib/sorting';

import { SYSTEM_COLUMN_FIELDS } from '../GeneratikTable.constants';
import {
  ContextMenuCollapseAllIcon,
  ContextMenuFilterIcon,
  ContextMenuHideColumnIcon,
  ContextMenuPinLeftIcon,
  ContextMenuPinRightIcon,
  ContextMenuResetColumnWidthIcon,
  ContextMenuSortIcon,
  ContextMenuUnpinIcon,
  ContextMenuUnsortIcon,
  ContextMenuZoomInIcon,
  ContextMenuZoomOutIcon,
} from '../GeneratikTable.icons';
import { useGeneratikTableContext } from '../GeneratikTableContext';
import { getContextMenuPaperSx } from '../contextMenuDesign';
import useGeneratikTableStore, { buildColumnWidthKey } from '../generatikTableStore';
import GroupBySubmenu from './GroupBySubmenu';
import SortBySubmenu from './SortBySubmenu';
import { buildSortMenuItem } from './sortMenuItem';
import { CONTEXT_MENU_TESTID, CONTEXT_MENU_TESTIDS } from './tableContextMenu.constants';
import { ContextMenuItem } from './tableContextMenu.styles';
import {
  collapseAllGroups,
  deriveSortOptions,
  expandAllGroups,
  filterByElement,
  hasExpandedGroups,
  hideColumn,
  resetColumnWidths,
  resolveFilterField,
  togglePinColumn,
  unpinColumn,
} from './tableContextMenuActions';
import { ContextMenuState } from './useTableContextMenu';
import { ZOOM_MAX, ZOOM_MIN } from './zoom';

interface Props {
  state: ContextMenuState | null;
  onClose: () => void;
}

// The menu is portaled, so a right-click anywhere on it (items, padding ring, submenus) bubbles
// through React to the table's own contextmenu handler, which would re-open the menu at the pointer.
const swallowContextMenu = (event: MouseEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const TableContextMenu = ({ state, onClose }: Props) => {
  const ctx = useGeneratikTableContext();
  const { sortActions } = ctx;
  const clearColumnWidths = useGeneratikTableStore((s) => s.clearColumnWidths);

  const target = state?.target;
  const isColumn = target?.kind === 'column';
  const field = target?.field ?? null;

  const column = useMemo(
    () => (field ? ctx.processedColumns.find((c) => c.field === field) : undefined),
    [ctx.processedColumns, field],
  );

  const widthKey = useMemo(
    () =>
      ctx.headersRequest
        ? buildColumnWidthKey(ctx.headersRequest.pageType, ctx.headersRequest.resourceId)
        : null,
    [ctx.headersRequest],
  );

  const canHide = !!column && column.hideable !== false && !SYSTEM_COLUMN_FIELDS.has(column.field);
  // `pinnable: false` is how MUI's own column menu drops its pinning item; this menu offers the
  // same actions, so it has to honour the same opt-out — otherwise a column a table pins
  // deliberately can still be unpinned from here.
  const pinDisabled = ctx.isTimelineActive || column?.pinnable === false;
  const pinnedSide =
    isColumn && field ? String(ctx.apiRef.current?.isColumnPinned?.(field) || '') : '';

  const sortableFields = ctx.sortOptions ?? deriveSortOptions(ctx.processedColumns);
  // Empty-area variant: pick a column from a submenu. Cell variant: act on the
  // clicked column directly (no submenu).
  const showEmptySort = !isColumn && !ctx.disableSortButton && sortableFields.length > 0;
  const showEmptyGroup =
    !isColumn &&
    !ctx.disableGroupButton &&
    (ctx.groupOptions?.length ?? 0) > 0 &&
    !!ctx.onGroupChange;

  const columnSortable = isColumn && !!field && sortableFields.some((o) => o.field === field);
  const showColumnSort = columnSortable && !ctx.disableSortButton;
  const sortItem = field ? buildSortMenuItem(sortActions, field) : null;

  const columnGroupable =
    isColumn && !!field && (ctx.groupOptions?.some((o) => o.field === field) ?? false);
  const showColumnGroup = columnGroupable && !ctx.disableGroupButton && !!ctx.onGroupChange;
  const isGroupedByColumn = isColumn && !!field && ctx.groupByField === field;

  // Collapse All toggles to Expand All once every group is collapsed. Guarded on
  // `state` so the closed menu doesn't scan every sorted row id on each re-render.
  const showExpandAll = !!state && ctx.treeDataEnabled && !hasExpandedGroups(ctx.apiRef);

  const showFilter = isColumn && !!ctx.columnFilterMap;
  const filterField =
    showFilter && field
      ? resolveFilterField(field, ctx.columnFilterMap!, ctx.filterFields)
      : undefined;
  const hasValue =
    target?.rawValue !== null && target?.rawValue !== undefined && target?.rawValue !== '';
  const filterEnabled = !!filterField && hasValue;

  const run = useCallback(
    (fn: () => void) => {
      fn();
      onClose();
    },
    [onClose],
  );

  if (!state) return null;

  const removeSortLevel = sortItem?.remove;

  const sortRow = showColumnSort && sortItem && (
    <ContextMenuItem
      disabled={sortItem.disabled}
      onClick={() => run(sortItem.apply)}
      data-testid={CONTEXT_MENU_TESTIDS.sortBy}
    >
      <ListItemIcon>
        <ContextMenuSortIcon />
      </ListItemIcon>
      <ListItemText>{ctx.t(sortItem.labelKey)}</ListItemText>
      {sortItem.direction === 'asc' && <ArrowUpwardIcon fontSize="small" />}
      {sortItem.direction === 'desc' && <ArrowDownwardIcon fontSize="small" />}
    </ContextMenuItem>
  );

  return (
    <Menu
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ top: state.position.y + 4, left: state.position.x + 2 }}
      slotProps={{
        // @ts-ignore
        paper: {
          sx: getContextMenuPaperSx,
          'data-testid': CONTEXT_MENU_TESTID,
          onContextMenu: swallowContextMenu,
        },
      }}
    >
      {isColumn && (
        <ContextMenuItem
          disabled={!canHide}
          onClick={() => run(() => field && hideColumn(ctx.apiRef, field))}
          data-testid={CONTEXT_MENU_TESTIDS.hideColumn}
        >
          <ListItemIcon>
            <ContextMenuHideColumnIcon />
          </ListItemIcon>
          <ListItemText>{ctx.t('hide_column')}</ListItemText>
        </ContextMenuItem>
      )}

      {isColumn &&
        (pinnedSide === 'left' ? (
          <ContextMenuItem
            disabled={pinDisabled}
            onClick={() => run(() => field && unpinColumn(ctx.apiRef, field))}
            data-testid={CONTEXT_MENU_TESTIDS.unpin}
          >
            <ListItemIcon>
              <ContextMenuUnpinIcon />
            </ListItemIcon>
            <ListItemText>{ctx.t('unpin')}</ListItemText>
          </ContextMenuItem>
        ) : (
          <ContextMenuItem
            disabled={pinDisabled}
            onClick={() => run(() => field && togglePinColumn(ctx.apiRef, field, 'left'))}
            data-testid={CONTEXT_MENU_TESTIDS.lockLeft}
          >
            <ListItemIcon>
              <ContextMenuPinLeftIcon />
            </ListItemIcon>
            <ListItemText>{ctx.t('lock_to_left')}</ListItemText>
          </ContextMenuItem>
        ))}
      {isColumn &&
        (pinnedSide === 'right' ? (
          <ContextMenuItem
            disabled={pinDisabled}
            onClick={() => run(() => field && unpinColumn(ctx.apiRef, field))}
            data-testid={CONTEXT_MENU_TESTIDS.unpin}
          >
            <ListItemIcon>
              <ContextMenuUnpinIcon />
            </ListItemIcon>
            <ListItemText>{ctx.t('unpin')}</ListItemText>
          </ContextMenuItem>
        ) : (
          <ContextMenuItem
            disabled={pinDisabled}
            onClick={() => run(() => field && togglePinColumn(ctx.apiRef, field, 'right'))}
            data-testid={CONTEXT_MENU_TESTIDS.lockRight}
          >
            <ListItemIcon>
              <ContextMenuPinRightIcon />
            </ListItemIcon>
            <ListItemText>{ctx.t('lock_to_right')}</ListItemText>
          </ContextMenuItem>
        ))}

      {sortRow && sortItem.disabled ? (
        <Tooltip
          title={ctx.t('sort_limit_reached', { count: MAX_SORT_COLUMNS })}
          placement="right"
          disableInteractive
        >
          <span>{sortRow}</span>
        </Tooltip>
      ) : (
        sortRow
      )}
      {showColumnSort && removeSortLevel && (
        <ContextMenuItem
          onClick={() => run(removeSortLevel)}
          data-testid={CONTEXT_MENU_TESTIDS.removeFromSort}
        >
          <ListItemIcon>
            <ContextMenuUnsortIcon />
          </ListItemIcon>
          <ListItemText>{ctx.t('remove_from_sort')}</ListItemText>
        </ContextMenuItem>
      )}
      {showEmptySort && <SortBySubmenu onAfterAction={onClose} />}

      {showColumnGroup && (
        <ContextMenuItem
          onClick={() => run(() => ctx.onGroupChange?.(isGroupedByColumn ? null : field))}
          data-testid={CONTEXT_MENU_TESTIDS.groupBy}
        >
          <ListItemIcon>
            <GroupIcon />
          </ListItemIcon>
          <ListItemText>{ctx.t(isGroupedByColumn ? 'ungroup' : 'group_by')}</ListItemText>
        </ContextMenuItem>
      )}
      {showEmptyGroup && <GroupBySubmenu onAfterAction={onClose} />}

      {showFilter && (
        <Tooltip
          title={
            !filterField
              ? ctx.t('column_not_filterable')
              : !hasValue
                ? ctx.t('filter_null_value')
                : ''
          }
          placement="right"
          disableInteractive
        >
          <span>
            <ContextMenuItem
              disabled={!filterEnabled}
              onClick={() =>
                run(
                  () =>
                    field &&
                    filterByElement(field, target?.rawValue, {
                      filterFields: ctx.filterFields,
                      filterValues: ctx.filterValues,
                      columnFilterMap: ctx.columnFilterMap!,
                      onFilterValuesChange: ctx.onFilterValuesChange,
                    }),
                )
              }
              data-testid={CONTEXT_MENU_TESTIDS.filterByElement}
            >
              <ListItemIcon>
                <ContextMenuFilterIcon />
              </ListItemIcon>
              <ListItemText>{ctx.t('filter_by_element')}</ListItemText>
            </ContextMenuItem>
          </span>
        </Tooltip>
      )}

      {(isColumn || showEmptySort || showEmptyGroup) && <Divider />}

      <ContextMenuItem
        onClick={() =>
          run(() =>
            resetColumnWidths({
              apiRef: ctx.apiRef,
              widthKey,
              clearColumnWidths,
              autosizeOptions: ctx.autosizeOptions,
            }),
          )
        }
        data-testid={CONTEXT_MENU_TESTIDS.resetWidths}
      >
        <ListItemIcon>
          <ContextMenuResetColumnWidthIcon />
        </ListItemIcon>
        <ListItemText>{ctx.t('reset_columns_width')}</ListItemText>
      </ContextMenuItem>

      <ContextMenuItem
        disabled={!ctx.treeDataEnabled}
        onClick={() => run(() => (showExpandAll ? expandAllGroups : collapseAllGroups)(ctx.apiRef))}
        data-testid={
          showExpandAll ? CONTEXT_MENU_TESTIDS.expandAll : CONTEXT_MENU_TESTIDS.collapseAll
        }
      >
        <ListItemIcon>
          {showExpandAll ? <UnfoldMoreIcon fontSize="small" /> : <ContextMenuCollapseAllIcon />}
        </ListItemIcon>
        <ListItemText>{ctx.t(showExpandAll ? 'expand_all' : 'collapse_all')}</ListItemText>
      </ContextMenuItem>

      <Divider />

      {/* Zoom buttons keep the menu open so it can be pressed repeatedly. */}
      <ContextMenuItem
        disabled={ctx.zoomLevel >= ZOOM_MAX}
        onClick={() => ctx.onZoomChange(ctx.zoomLevel + 1)}
        data-testid={CONTEXT_MENU_TESTIDS.zoomIn}
      >
        <ListItemIcon>
          <ContextMenuZoomInIcon />
        </ListItemIcon>
        <ListItemText>{ctx.t('zoom_in')}</ListItemText>
      </ContextMenuItem>
      <ContextMenuItem
        disabled={ctx.zoomLevel <= ZOOM_MIN}
        onClick={() => ctx.onZoomChange(ctx.zoomLevel - 1)}
        data-testid={CONTEXT_MENU_TESTIDS.zoomOut}
      >
        <ListItemIcon>
          <ContextMenuZoomOutIcon />
        </ListItemIcon>
        <ListItemText>{ctx.t('zoom_out')}</ListItemText>
      </ContextMenuItem>
      {ctx.zoomLevel !== 0 && (
        <ContextMenuItem
          onClick={() => ctx.onZoomChange(0)}
          data-testid={CONTEXT_MENU_TESTIDS.resetZoom}
        >
          <ListItemIcon>
            <RestartAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{ctx.t('reset_zoom')}</ListItemText>
        </ContextMenuItem>
      )}
    </Menu>
  );
};

export default TableContextMenu;
